"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EthersEventUtils = exports.EthersTransactionUtils = exports.EthersContractHandle = void 0;
const ethers_1 = require("ethers");
const contracts_1 = require("@ethersproject/contracts");
const utils_1 = require("../utils");
const address_1 = require("./address");
/**
 * Ethers-based contract handle.
 */
class EthersContractHandle {
    /**
     * @param config Configuration for contract instance initialization.
     * @param deployment Contract Deployment artifact.
     * @param totalRetryAttempts Number of retries for ethereum requests.
     */
    constructor(config, deployment, totalRetryAttempts = 3) {
        var _a, _b;
        this._instance = new contracts_1.Contract((_a = config.address) !== null && _a !== void 0 ? _a : ethers_1.utils.getAddress(deployment.address), `${JSON.stringify(deployment.abi)}`, config.signerOrProvider);
        this._deployedAtBlockNumber =
            (_b = config.deployedAtBlockNumber) !== null && _b !== void 0 ? _b : deployment.receipt.blockNumber;
        this._totalRetryAttempts = totalRetryAttempts;
    }
    /**
     * Get address of the contract instance.
     * @returns Address of this contract instance.
     */
    getAddress() {
        return address_1.EthereumAddress.from(this._instance.address);
    }
    /**
     * Get events emitted by the Ethereum contract.
     * It starts searching from provided block number. If the {@link GetEvents.Options#fromBlock}
     * option is missing it looks for a contract's defined property
     * {@link _deployedAtBlockNumber}. If the property is missing starts searching
     * from block `0`.
     * @param eventName Name of the event.
     * @param options Options for events fetching.
     * @param filterArgs Arguments for events filtering.
     * @returns Array of found events.
     */
    async getEvents(eventName, options, ...filterArgs) {
        var _a;
        return (0, utils_1.backoffRetrier)((_a = options === null || options === void 0 ? void 0 : options.retries) !== null && _a !== void 0 ? _a : this._totalRetryAttempts)(async () => {
            var _a;
            return await EthersEventUtils.getEvents(this._instance, this._instance.filters[eventName](...filterArgs), (_a = options === null || options === void 0 ? void 0 : options.fromBlock) !== null && _a !== void 0 ? _a : this._deployedAtBlockNumber, options === null || options === void 0 ? void 0 : options.toBlock, options === null || options === void 0 ? void 0 : options.batchedQueryBlockInterval, options === null || options === void 0 ? void 0 : options.logger);
        });
    }
}
exports.EthersContractHandle = EthersContractHandle;
/**
 * Ethers-based utilities for transactions.
 */
var EthersTransactionUtils;
(function (EthersTransactionUtils) {
    /**
     * Sends ethereum transaction with retries.
     * @param fn Function to execute with retries.
     * @param retries The number of retries to perform before bubbling the failure out.
     * @param logger A logger function to pass execution messages.
     * @param nonRetryableErrors List of error messages that if returned from executed
     *        function, should break the retry loop and return immediately.
     * @returns Result of function execution.
     * @throws An error returned by function execution. An error thrown by the executed
     *         function is processed by {@link resolveEthersError} function to resolve
     *         the revert message in case of a transaction revert.
     */
    async function sendWithRetry(fn, retries, logger, nonRetryableErrors) {
        return (0, utils_1.backoffRetrier)(retries, 1000, logger, nonRetryableErrors ? (0, utils_1.skipRetryWhenMatched)(nonRetryableErrors) : undefined)(async () => {
            try {
                return await fn();
            }
            catch (err) {
                throw resolveEthersError(err);
            }
        });
    }
    EthersTransactionUtils.sendWithRetry = sendWithRetry;
    /**
     * Takes an error and tries to resolve a revert message if the error is related
     * to reverted transaction.
     * @param err Error to process.
     * @returns Error with a revert message or the input error when the error could
     *          not be resolved successfully.
     */
    function resolveEthersError(err) {
        const isEthersError = (obj) => {
            return "reason" in obj && "code" in obj && "error" in obj;
        };
        if (isEthersError(err) && err !== null) {
            // Ethers errors are nested. The parent UNPREDICTABLE_GAS_LIMIT has a general
            // reason "cannot estimate gas; transaction may fail or may require manual gas limit",
            if (err.code === "UNPREDICTABLE_GAS_LIMIT") {
                if (typeof isEthersError(err["error"]) && err["error"] !== null) {
                    // The nested error is expected to contain a reason property with a message
                    // of the transaction revert.
                    return new Error(err["error"].reason);
                }
            }
        }
        return err;
    }
})(EthersTransactionUtils = exports.EthersTransactionUtils || (exports.EthersTransactionUtils = {}));
/**
 * Ethers-based utilities for events.
 */
var EthersEventUtils;
(function (EthersEventUtils) {
    const GET_EVENTS_BLOCK_INTERVAL = 10000;
    /**
     * Looks up all existing events defined by the {@link event} filter on
     * {@link sourceContract}, searching past blocks and then returning them.
     * Does not wait for any new events. It starts searching from provided block number.
     * If the {@link fromBlock} is missing it starts searching from block `0`.
     * It pulls events in one `getLogs` call. If the call fails it fallbacks to querying
     * events in batches of {@link batchedQueryBlockInterval} blocks. If the parameter
     * is not set it queries in {@link GET_EVENTS_BLOCK_INTERVAL} blocks batches.
     * @param sourceContract The contract instance that emits the event.
     * @param event The event filter to query.
     * @param fromBlock Starting block for events search.
     * @param toBlock Ending block for events search.
     * @param batchedQueryBlockInterval Block interval for batched events pulls.
     * @param logger A logger function to pass execution messages.
     * @returns A promise that will be fulfilled by the list of event objects once
     *          they are found.
     */
    async function getEvents(sourceContract, event, fromBlock = 0, toBlock = "latest", batchedQueryBlockInterval = GET_EVENTS_BLOCK_INTERVAL, logger = console.debug) {
        return new Promise(async (resolve, reject) => {
            let resultEvents = [];
            try {
                resultEvents = await sourceContract.queryFilter(event, fromBlock, toBlock);
            }
            catch (err) {
                logger(`switching to partial events pulls; ` +
                    `failed to get events in one request from contract: [${event.address}], ` +
                    `fromBlock: [${fromBlock}], toBlock: [${toBlock}]: [${err}]`);
                try {
                    if (typeof toBlock === "string") {
                        toBlock = (await sourceContract.provider.getBlock(toBlock)).number;
                    }
                    let batchStartBlock = fromBlock;
                    while (batchStartBlock <= toBlock) {
                        let batchEndBlock = batchStartBlock + batchedQueryBlockInterval;
                        if (batchEndBlock > toBlock) {
                            batchEndBlock = toBlock;
                        }
                        logger(`executing partial events pull from contract: [${event.address}], ` +
                            `fromBlock: [${batchStartBlock}], toBlock: [${batchEndBlock}]`);
                        const foundEvents = await sourceContract.queryFilter(event, batchStartBlock, batchEndBlock);
                        resultEvents = resultEvents.concat(foundEvents);
                        logger(`fetched [${foundEvents.length}] events, has ` +
                            `[${resultEvents.length}] total`);
                        batchStartBlock = batchEndBlock + 1;
                    }
                }
                catch (error) {
                    return reject(error);
                }
            }
            return resolve(resultEvents);
        });
    }
    EthersEventUtils.getEvents = getEvents;
})(EthersEventUtils = exports.EthersEventUtils || (exports.EthersEventUtils = {}));
//# sourceMappingURL=adapter.js.map